<template>
    <v-container>
        <div class="showfilters" style="display: flex; flex-wrap: wrap; justify-content: space-between">
            <span class="text-subtitle"> {{ $t('recognitions.searchUserToRecognize') }}</span>
            <c-btn
                v-if="recognizableUsers.length"
                mdi-icon="mdi-filter"
                :title="showFilterUsers ? $t('common.hideFilters') : $t('common.showFilters')"
                @click="showFilterUsers = !showFilterUsers"
            />
        </div>
        <div v-if="showFilterUsers && recognizableUsers.length">
            <div class="menu-filter">
                <div
                    v-for="level in levels"
                    :key="level.key"
                >
                    <span class="text-h6">{{ $t(level.label) }}</span>
                    <v-checkbox
                        v-model="selectAll[level.key]"
                        :label="selectAll[level.key] ? $t('common.selectAll') : $t('common.unselectAll')"
                        hide-details
                        class="mt-1 selectAllCHK"
                        @change="selectAllOptions(level.key, $event)"
                    />
                    <div class="menu-filter-options">
                        <v-checkbox
                            v-for="option in cascade.options[level.key]"
                            :key="option"
                            v-model="filter[level.key]"
                            :label="option || $t('recognitions.filterUsers.empty')"
                            :value="option"
                            hide-details
                            class="mt-1"
                            @change="checkSelectAll(level.key)"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div>
            <v-autocomplete
                v-model="person_"
                class="mt-0 pt-0"
                :items="usersFiltered"
                color="blue-grey lighten-2"
                :label="autocompleteLabel"
                :disabled="!loading && !recognizableUsers.length"
                :hint="$t('recognitions.usersFiltered', {persons: usersFiltered.length})"
                persistent-hint
                single-line
                hide-details
                item-text="attributes.name"
                item-value="id"
                :filter="filterPersons"
            >
                <template
                    v-slot:selection="data"
                >
                    <v-list-item-content v-text="data.item.attributes.name + ' ' + data.item.attributes.lastname" />
                </template>
                <template v-slot:item="data">
                    <template v-if="typeof data.item !== 'object'">
                        <v-list-item-content v-text="data.item" />
                    </template>
                    <template v-else>
                        <v-list-item-content>
                            <v-list-item-title v-html="data.item.attributes.name + ' ' + data.item.attributes.lastname" />
                            <v-list-item-subtitle v-text="orgSummary(data.item)" />
                        </v-list-item-content>
                    </template>
                </template>
            </v-autocomplete>
        </div>
    </v-container>
</template>

<script>

// Columnas del Excel de usuarios por las que se filtra, en cascada: las
// opciones de cada nivel salen solo de los trabajadores que pasan los niveles
// anteriores.
const LEVELS = [
    { key: 'pais', label: 'recognitions.filterUsers.country' },
    { key: 'centro', label: 'recognitions.filterUsers.center' },
    { key: 'sociedad', label: 'recognitions.filterUsers.society' },
    { key: 'uo1', label: 'recognitions.filterUsers.ou' },
    { key: 'uo2', label: 'recognitions.filterUsers.ou2' },
    { key: 'uo3', label: 'recognitions.filterUsers.ou3' }
];

const SUMMARY_KEYS = ['pais', 'centro', 'sociedad', 'uo1'];

function byLevel(makeValue) {
    return LEVELS.reduce((acc, level) => {
        acc[level.key] = makeValue();
        return acc;
    }, {});
}

function orgValue(user, key) {
    return String(user.attributes[key] ?? '').trim();
}

// Orden alfabetico en castellano y la casilla de "sin dato" al final.
function sortOptions(values) {
    const filled = values.filter(value => value !== '').sort((a, b) => a.localeCompare(b, 'es'));

    return values.includes('') ? [...filled, ''] : filled;
}

export default {
    name: "SelectPerson",
    components: {
        CBtn: () => import('../../components/base/c-btn.vue')
    },
    props: {
        person: {
            type: String,
            default: ''
        }
    },
    data: function () {
        return {
            levels: LEVELS,
            users: [],
            loading: true,
            filter: byLevel(() => []),
            showFilterUsers: false,
            selectAll: byLevel(() => true)
        }
    },
    computed: {
        // Solo los trabajadores que vienen del Excel: las cuentas tecnicas y de
        // administracion se dan de alta a mano, sin pais, y ni se pueden
        // reconocer ni deben aportar opciones a los filtros.
        recognizableUsers() {
            return this.users.filter(user => orgValue(user, 'pais') !== '');
        },
        cascade() {
            const options = {};
            let pool = this.recognizableUsers;

            LEVELS.forEach(({ key }) => {
                options[key] = sortOptions([...new Set(pool.map(user => orgValue(user, key)))]);

                const selected = new Set(this.filter[key]);
                pool = pool.filter(user => selected.has(orgValue(user, key)));
            });

            return { options, users: pool };
        },
        usersFiltered() {
            return this.cascade.users;
        },
        autocompleteLabel() {
            if (this.loading) return this.$t('recognitions.loadingUsersMSG');
            if (!this.recognizableUsers.length) return this.$t('recognitions.noUsersToRecognize');

            return this.$t('recognitions.selectUserToRecognize');
        },
        person_: {
            get() {
                return this.person;
            },
            set(val) {
                this.$emit('update:person', val);
            }
        }
    },
    async mounted() {
        // Siempre se pide la lista al servidor: con la de la cache, tras un
        // cierre de anio o una importacion seguian saliendo los trabajadores
        // archivados y sus centros, sociedades y UO en los filtros.
        const users = await this.$store.dispatch('cacheRecognitios/loadUsers', { force: true });

        this.users = Array.isArray(users) ? users : [];
        this.loading = false;

        // Todo seleccionado de entrada, nivel a nivel para que cada uno vea ya
        // la seleccion del anterior.
        LEVELS.forEach(({ key }) => {
            this.filter[key] = [...this.cascade.options[key]];
        });
    },
    methods: {
        filterPersons(item, queryText, itemText) {
            const fullText = item.attributes.name + ' ' + item.attributes.lastname;

            return (
                fullText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) >
                -1
            );
        },
        orgSummary(user) {
            return SUMMARY_KEYS.map(key => orgValue(user, key)).filter(value => value !== '').join(' - ');
        },
        // El v-model de un nivel puede guardar valores que ya no se ven (los de
        // un centro deseleccionado mas arriba): "todos" es que esten marcadas
        // todas las opciones visibles.
        checkSelectAll(key) {
            const selected = new Set(this.filter[key]);

            this.selectAll[key] = this.cascade.options[key].every(option => selected.has(option));
        },
        selectAllOptions(key, checked) {
            this.filter[key] = checked ? [...this.cascade.options[key]] : [];
        }
    }
}
</script>

<style scoped lang="scss">
.menu-filter{
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
      row-gap: 20px;

  .text-h6{
    border-bottom: 1px solid #00b5e2;
    display: block;
    margin-left: 5px;
    margin-right: 5px;
  }

  // UO2 y UO3 traen cientos de valores: cada columna se desplaza por dentro en
  // vez de estirar la pagina.
  .menu-filter-options{
    max-height: 320px;
    overflow-y: auto;
    overflow-x: hidden;

    // Valores como "GE/HONEYWELL/SAFRAN" no tienen espacios donde partir.
    ::v-deep .v-label{
      overflow-wrap: anywhere;
    }
  }

}
</style>
